'use client';

import { Button } from '@app/app/_components/Button/Button';
import { Card } from '@app/app/_components/Card/Card';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Message } from '@app/app/_components/Message/Message';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { useTranslations } from 'next-intl';

interface Props {
    name: string;
    surname: string;
    personalCode: string;
    age: number;
    index?: number;
    edit?: () => void;
    ageMismatch?: boolean;
    changeAge: () => void;
    isCompany?: boolean;
    companyName?: string;
}

export function PolicyholderCard({
    name,
    surname,
    personalCode,
    age,
    index,
    edit,
    ageMismatch,
    changeAge,
    isCompany,
    companyName,
}: Props) {
    const t = useTranslations();

    const title = isCompany
        ? t('eshop.personalAccidents.policyholder')
        : t('eshop.personalAccidents.primaryPersonWithAge', { age });

    return (
        <Card className="gap-8 p-6">
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {index !== undefined && (
                            <div className="flex size-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-400 text-sm text-white">
                                {index + 1}
                            </div>
                        )}
                        <Heading level="h3">
                            {title} {!isCompany && `(${t('eshop.personalAccidents.policyholder')})`}
                        </Heading>
                    </div>
                    <TextButton preventDefault onClick={edit} className="hover:text-primary-600 text-primary-500">
                        {t('general.edit')}
                    </TextButton>
                </div>

                <div className="flex flex-wrap gap-6">
                    {isCompany ? (
                        <>
                            <ReadOnlyField label={t('profile.companyNameLabel')} className="flex-1">
                                {companyName}
                            </ReadOnlyField>
                            <ReadOnlyField label={t('profile.companyCodeLabel')} className="flex-1">
                                {personalCode}
                            </ReadOnlyField>
                        </>
                    ) : (
                        <>
                            <ReadOnlyField label={t('eshop.personalAccidents.name')} className="flex-1">
                                {name}
                            </ReadOnlyField>
                            <ReadOnlyField label={t('eshop.personalAccidents.surname')} className="flex-1">
                                {surname}
                            </ReadOnlyField>
                            <ReadOnlyField label={t('eshop.personalAccidents.personalCode')} className="flex-1">
                                {personalCode}
                            </ReadOnlyField>
                        </>
                    )}
                </div>
                {ageMismatch && (
                    <Message
                        variant="danger"
                        containerClassName="items-center"
                        addon={
                            <Button
                                onClick={changeAge}
                                preventDefault
                                rightAddon={<ArrowRightIcon className="size-4" />}
                                size="tiny">
                                {t('general.change')}
                            </Button>
                        }>
                        {t('eshop.personalAccidents.ageMismatchError')}
                    </Message>
                )}
            </div>
        </Card>
    );
}
